import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import {
  getCogoIdentityBase,
  getStudioAuthProvider,
  withCogoDevProxyHeaders,
} from '@/services/cogo-platform-config'
import { sessionNeedsRefresh } from '@/services/session-expiry'

type AuthEvent = 'INITIAL_SESSION' | 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | 'USER_UPDATED'
type AuthListener = (event: AuthEvent, session: Session | null) => void

interface AuthResponse {
  data: { session: Session | null; user: User | null }
  error: Error | null
}

interface CogoAuthSessionResponse {
  access_token: string
  refresh_token?: string
  token_type?: string
  expires_in?: number
  user: {
    id: string
    email: string
    role?: string
  }
}

const COGO_AUTH_STORAGE_KEY = 'cogo-studio-cogo-auth-session'

export const AUTH_PROVIDER =
  getStudioAuthProvider()

export const isCogoAuthProvider = AUTH_PROVIDER === 'cogo'

const cogoIdentityBase = getCogoIdentityBase()

const listeners = new Set<AuthListener>()
let cogoSessionRefreshInFlight: Promise<Session | null> | null = null

function cogoIdentityHeaders(init?: HeadersInit): Headers {
  return withCogoDevProxyHeaders(init)
}

export async function getStudioSession(): Promise<{ data: { session: Session | null }; error: Error | null }> {
  if (!isCogoAuthProvider) return supabase.auth.getSession()
  try {
    return { data: { session: await getValidCogoSession() }, error: null }
  } catch (error) {
    // A proxy restart, HMR reconnect, or short DGX network interruption does
    // not invalidate the refresh credential that is still stored locally.
    // Keep the authenticated shell mounted while callers surface the request
    // error; getStudioAccessToken deliberately refuses to hand out the stale
    // access token when `error` is present.
    const preservedSession = readCogoSession({ allowExpired: true })
    return {
      data: { session: preservedSession },
      error: error instanceof Error ? error : new Error('Cogo session refresh failed'),
    }
  }
}

export async function getStudioUser(): Promise<{ data: { user: User | null }; error: Error | null }> {
  if (!isCogoAuthProvider) return supabase.auth.getUser()
  const session = await getValidCogoSession()
  if (!session?.access_token) return { data: { user: null }, error: null }

  const response = await fetch(`${cogoIdentityBase}/auth/me`, {
    headers: cogoIdentityHeaders({ Authorization: `Bearer ${session.access_token}` }),
  })
  if (!response.ok) {
    return { data: { user: null }, error: new Error(`Cogo getUser failed: ${response.status}`) }
  }
  const body = (await response.json()) as { user?: { id: string; email: string; role?: string } }
  const user = body.user ? toSupabaseUser(body.user, session.access_token) : null
  return { data: { user }, error: null }
}

export function onStudioAuthStateChange(callback: AuthListener): {
  data: { subscription: { unsubscribe: () => void } }
} {
  if (!isCogoAuthProvider) return supabase.auth.onAuthStateChange(callback as never)
  listeners.add(callback)
  queueMicrotask(() => {
    void getStudioSession().then(({ data }) => callback('INITIAL_SESSION', data.session))
  })
  return {
    data: {
      subscription: {
        unsubscribe: () => listeners.delete(callback),
      },
    },
  }
}

export async function signInStudioWithPassword(input: {
  email: string
  password: string
}): Promise<AuthResponse> {
  if (!isCogoAuthProvider) return supabase.auth.signInWithPassword(input)
  return cogoPasswordRequest('/auth/login', input)
}

export async function signUpStudio(input: {
  email: string
  password: string
}): Promise<AuthResponse> {
  if (!isCogoAuthProvider) return supabase.auth.signUp(input)
  return cogoPasswordRequest('/auth/signup', input)
}

/**
 * Retires the password the appliance issued during installation.
 *
 * Unauthenticated by design: sign-in mints no session while that password
 * stands, so requiring one here would leave the holder unable to clear it.
 * Only meaningful on an appliance -- the cloud provider has its own recovery.
 */
export async function changeStudioPassword(input: {
  email: string
  currentPassword: string
  newPassword: string
}): Promise<AuthResponse> {
  if (!isCogoAuthProvider) {
    return {
      data: { session: null, user: null },
      error: new Error('Password rotation is handled by the identity provider'),
    }
  }
  return cogoPasswordRequest('/auth/password/change', input)
}

export async function signOutStudio(): Promise<{ error: Error | null }> {
  if (!isCogoAuthProvider) return supabase.auth.signOut({ scope: 'global' })

  const session = readCogoSession({ allowExpired: true })
  clearCogoSession()
  notify('SIGNED_OUT', null)

  if (session?.refresh_token) {
    await fetch(`${cogoIdentityBase}/auth/logout`, {
      method: 'POST',
      headers: cogoIdentityHeaders({ 'content-type': 'application/json' }),
      body: JSON.stringify({ refresh_token: session.refresh_token }),
    }).catch(() => undefined)
  }

  return { error: null }
}

export async function signInStudioWithGoogle(): Promise<{ error: Error | null }> {
  if (!isCogoAuthProvider) {
    return supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/studio` },
    })
  }

  return {
    error: new Error('Google sign-in is not available for the local Cogo identity provider yet.'),
  }
}

export async function signInStudioWithGitHub(): Promise<{ error: Error | null }> {
  if (!isCogoAuthProvider) {
    return supabase.auth.signInWithOAuth({
      provider: 'github',
      options: { redirectTo: `${window.location.origin}/studio` },
    })
  }

  return {
    error: new Error('GitHub sign-in is not available for the local Cogo identity provider yet.'),
  }
}

export async function getStudioAccessToken(): Promise<string | null> {
  const { data, error } = await getStudioSession()
  if (error || !data.session) return null
  return data.session.access_token
}

export async function getStudioUserId(): Promise<string | null> {
  const token = await getStudioAccessToken()
  const sub = token ? decodeJwtSub(token) : null
  if (sub) return sub

  const { data } = await getStudioUser()
  return data.user?.id ?? null
}

/**
 * Carries the identity service's `error` code out of a failed request.
 *
 * The appliance can now refuse a sign-in for a reason that is not a failure:
 * `password_change_required` means the credential was correct but is the one
 * the appliance issued itself, and the screen has to move to a replacement
 * step rather than show something red. That decision cannot be made from a
 * status code alone.
 */
export class CogoAuthError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
    readonly status: number,
  ) {
    super(message)
    this.name = 'CogoAuthError'
  }
}

async function cogoPasswordRequest(
  path: string,
  input: Record<string, string>,
): Promise<AuthResponse> {
  const response = await fetch(`${cogoIdentityBase}${path}`, {
    method: 'POST',
    headers: cogoIdentityHeaders({ 'content-type': 'application/json' }),
    body: JSON.stringify(input),
  })
  if (!response.ok) {
    // The service sends `{ error, message }`. Discarding both left every
    // refusal reading "Cogo auth request failed: 409", which tells the person
    // in front of the appliance nothing about what to do next.
    const failure = (await response.json().catch(() => ({}))) as {
      error?: string
      message?: string
    }
    return {
      data: { session: null, user: null },
      error: new CogoAuthError(
        failure.message || failure.error || `Cogo auth request failed: ${response.status}`,
        failure.error ?? null,
        response.status,
      ),
    }
  }

  const body = (await response.json()) as CogoAuthSessionResponse
  const session = toSupabaseSession(body)
  writeCogoSession(session)
  notify('SIGNED_IN', session)
  return { data: { session, user: session.user }, error: null }
}

async function getValidCogoSession(): Promise<Session | null> {
  const session = readCogoSession({ allowExpired: true })
  if (!session) return null
  if (!isSessionExpired(session)) return session
  if (!session.refresh_token) {
    clearCogoSession()
    notify('SIGNED_OUT', null)
    return null
  }
  if (cogoSessionRefreshInFlight) return cogoSessionRefreshInFlight

  const request = refreshCogoSession(session)
  cogoSessionRefreshInFlight = request
  try {
    return await request
  } finally {
    if (cogoSessionRefreshInFlight === request) {
      cogoSessionRefreshInFlight = null
    }
  }
}

async function refreshCogoSession(session: Session): Promise<Session | null> {
  const response = await fetch(`${cogoIdentityBase}/auth/refresh`, {
    method: 'POST',
    headers: cogoIdentityHeaders({ 'content-type': 'application/json' }),
    body: JSON.stringify({ refresh_token: session.refresh_token }),
  })
  if (!response.ok) {
    if (isDefinitiveRefreshRejection(response.status)) {
      // Refresh tokens rotate. A full reload/HMR or a second tab can complete
      // a refresh with this token while this request is still in flight. Its
      // later 401 belongs to the old token and must not delete the newer
      // session now present in shared localStorage.
      const latestSession = readCogoSession({ allowExpired: true })
      if (
        latestSession?.refresh_token &&
        latestSession.refresh_token !== session.refresh_token
      ) {
        return latestSession
      }

      clearCogoSession()
      notify('SIGNED_OUT', null)
      return null
    }

    throw new CogoAuthError(
      `Cogo session refresh temporarily failed: ${response.status}`,
      null,
      response.status,
    )
  }

  const body = (await response.json()) as CogoAuthSessionResponse
  const refreshed = toSupabaseSession(body)
  writeCogoSession(refreshed)
  notify('TOKEN_REFRESHED', refreshed)
  return refreshed
}

function isDefinitiveRefreshRejection(status: number): boolean {
  // Identity currently uses 401 for an invalid/expired rotating token and 403
  // for a disabled account. A malformed refresh request (400) cannot recover
  // without a new login. Proxy/routing/server availability statuses are not
  // proof that the credential itself is invalid, so they preserve storage.
  return status === 400 || status === 401 || status === 403
}

function toSupabaseSession(body: CogoAuthSessionResponse): Session {
  const expiresIn = body.expires_in ?? 900
  const expiresAt = Math.floor(Date.now() / 1000) + expiresIn
  return {
    access_token: body.access_token,
    refresh_token: body.refresh_token ?? '',
    token_type: body.token_type ?? 'Bearer',
    expires_in: expiresIn,
    expires_at: expiresAt,
    user: toSupabaseUser(body.user, body.access_token),
  } as Session
}

function toSupabaseUser(user: CogoAuthSessionResponse['user'], accessToken: string): User {
  return {
    id: user.id,
    email: user.email,
    role: user.role ?? 'authenticated',
    aud: 'authenticated',
    app_metadata: { provider: 'cogo', role: user.role },
    user_metadata: {},
    identities: [],
    created_at: new Date(0).toISOString(),
    updated_at: new Date().toISOString(),
    factors: null,
    is_anonymous: false,
    phone: '',
    confirmed_at: new Date(0).toISOString(),
    last_sign_in_at: new Date().toISOString(),
    recovery_sent_at: undefined,
    email_change_sent_at: undefined,
    new_email: undefined,
    invited_at: undefined,
    action_link: undefined,
    access_token: accessToken,
  } as unknown as User
}

function readCogoSession(options: { allowExpired?: boolean } = {}): Session | null {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(COGO_AUTH_STORAGE_KEY)
  if (!raw) return null
  try {
    const session = JSON.parse(raw) as Session
    if (!options.allowExpired && isSessionExpired(session)) {
      clearCogoSession()
      return null
    }
    return session
  } catch {
    clearCogoSession()
    return null
  }
}

function isSessionExpired(session: Session): boolean {
  // Refreshes BEFORE expiry, not after. Testing "already past" hands out a
  // token with a second left, which then expires while the request travels --
  // measured on the appliance as a 200 followed by a 401 on the same session
  // with no refresh in between. See session-expiry.ts.
  return sessionNeedsRefresh(session)
}

function writeCogoSession(session: Session): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(COGO_AUTH_STORAGE_KEY, JSON.stringify(session))
}

function clearCogoSession(): void {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(COGO_AUTH_STORAGE_KEY)
}

function notify(event: AuthEvent, session: Session | null): void {
  for (const listener of listeners) listener(event, session)
}

function decodeJwtSub(jwt: string): string | null {
  try {
    const [, payload] = jwt.split('.')
    if (!payload) return null
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=')
    const parsed = JSON.parse(atob(padded)) as { sub?: string }
    return parsed.sub ?? null
  } catch {
    return null
  }
}
